import React, { useState } from 'react';
import { Modal, Upload, message, Tag, Tooltip, Select } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { snackbar } from '../../Snackbar';
import { useLocationByOrgIdQuery } from '../../../../graphql/query/locationsByOrgId';
import axios from 'axios';

const { Dragger } = Upload;

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 24px;
  }
`;

const ModalTitle = () => (
  <div style={{ display: 'flex', textAlign: 'center', justifyContent: 'center', gap: '8px' }}>
    <h2 style={{ margin: 0 }}>Upload Sales Attribution</h2>
    <Tooltip title="This feature is in beta. Please ensure your CSV file follows the required format. Reach out to support if you have any questions.">
      <Tag
        style={{
          backgroundColor: '#004aad',
          color: 'white',
          borderRadius: '12px',
          fontSize: '0.9rem',
          padding: '2px 8px',
          fontWeight: 'bold',
        }}
      >
        BETA
      </Tag>
    </Tooltip>
  </div>
);

interface MarketplaceSalesAttributionModalProps {
  isOpen: boolean;
  onCancel: () => void;
  organizationId: string;
  locationId?: string;
}

export const MarketplaceSalesAttributionModal: React.FC<MarketplaceSalesAttributionModalProps> = ({
  isOpen,
  onCancel,
  organizationId,
  locationId,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<string | null>(locationId || null);
  const [uploading, setUploading] = useState(false);

  const { locations } = useLocationByOrgIdQuery(organizationId);

  const handleUpload = async ({ file, onSuccess, onError }: any) => {
    if (!selectedLocation) {
      snackbar.error({
        message: 'Please select a location first',
      });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('locationId', selectedLocation);

      await axios.post('/api/marketplace-listing/uploadSalesAttribution', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onSuccess();
      message.success(
        'Sales attribution file uploaded successfully. Attribution typically takes 3-5 business days to complete.'
      );
      onCancel();
    } catch (error) {
      onError(error);
      snackbar.error({
        message: 'Failed to upload sales attribution file',
      });
    } finally {
      setUploading(false);
    }
  };

  const props = {
    name: 'file',
    multiple: false,
    accept: '.csv',
    customRequest: handleUpload,
    showUploadList: false,
    disabled: uploading || !selectedLocation,
  };

  return (
    <StyledModal title={<ModalTitle />} open={isOpen} onCancel={onCancel} footer={null} width={600}>
      <div style={{ marginBottom: '24px' }}>
        <Select
          style={{ width: '100%' }}
          placeholder="Select a location"
          value={selectedLocation}
          onChange={setSelectedLocation}
          options={locations.map((location) => ({
            label: location.name,
            value: location.id,
          }))}
        />
      </div>

      <p>Please upload a CSV file containing the following information:</p>
      <ul>
        <li>VIN (Vehicle Identification Number)</li>
        <li>First Name</li>
        <li>Last Name</li>
      </ul>
      <Dragger {...props}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag CSV file to this area to upload</p>
        <p className="ant-upload-hint">
          Only CSV files are accepted. The file should contain VIN, First Name, and Last Name columns.
        </p>
      </Dragger>
    </StyledModal>
  );
};
